import type { ReactNode } from "react";
import {
  DomainCard,
  Eyebrow,
  cn,
  domainClasses,
  type Domain,
} from "@appcaire/ui";

// Brand 2.2: a text-led card (process, persona, resources, proof, benefits)
// built on DomainCard. `domain` says what kind of information the card is
// (planning, care, continuity, attention, human, society); it defaults to
// `human`, the old coral identity. `surface="tint"` fills the card with the
// domain tint; the default white card keeps grids calm.

export type EditorialCardProps = {
  icon?: ReactNode;
  eyebrow?: string;
  title: string;
  body: string;
  /** A link cue (e.g. <CardLink>). */
  footer?: ReactNode;
  domain?: Domain;
  surface?: "white" | "tint";
  /** Retired with the coral edge; kept so old callers still compile. */
  accentBar?: boolean;
  className?: string;
};

export function EditorialCard({
  icon,
  eyebrow,
  title,
  body,
  footer,
  domain = "human",
  surface = "white",
  className,
}: EditorialCardProps) {
  const d = domainClasses[domain];
  return (
    <DomainCard
      domain={domain}
      surface={surface}
      interactive={Boolean(footer)}
      className={cn("h-full gap-0 p-5", footer && "group", className)}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            "mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl text-caire-on-solid [&_svg]:h-[22px] [&_svg]:w-[22px]",
            d.solid,
          )}
        >
          {icon}
        </span>
      ) : null}
      {eyebrow ? (
        <Eyebrow tone={domain} as="div" className="text-[11px]">
          {eyebrow}
        </Eyebrow>
      ) : null}
      <h3 className="mt-1.5 text-[18px] font-bold leading-snug tracking-[-0.01em] text-caire-ink">
        {title}
      </h3>
      <p className="mt-2.5 flex-1 text-sm leading-[1.6] text-caire-ink/80">
        {body}
      </p>
      {footer ? <div className="mt-4">{footer}</div> : null}
    </DomainCard>
  );
}

export default EditorialCard;
